import { useMemo, useState } from "react";
import type { Person, Skill } from "../services/api";

interface SkillsSelectorProps {
  person: Person;
  availableSkills: Skill[];
  onSave: (skillCodes: string[]) => void;
  onClose: () => void;
}

export function SkillsSelector({ person, availableSkills, onSave, onClose }: SkillsSelectorProps) {
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set(person.skills ?? []));

  const assignableSkills = useMemo(
    () =>
      availableSkills.filter(
        (skill) => (skill.group_type ?? "").toUpperCase() !== "CERTIFICATION",
      ),
    [availableSkills],
  );

  const filteredSkills = useMemo(() => {
    const term = filter.toLowerCase();
    return assignableSkills.filter(
      (skill) =>
        skill.skill_code.toLowerCase().includes(term) ||
        (skill.name ?? "").toLowerCase().includes(term) ||
        (skill.tags ?? []).some((tag) => tag.toLowerCase().includes(term)),
    );
  }, [assignableSkills, filter]);

  function toggleSkill(code: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  }

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="modal">
        <header className="modal-header">
          <h3>Assign Skills – {person.name}</h3>
          <button type="button" onClick={onClose} className="ghost small">
            Close
          </button>
        </header>
        <div className="modal-body">
          <input
            type="search"
            placeholder="Search skills by code, name, or tag"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          />
          <div className="skills-list">
            {filteredSkills.map((skill) => {
              const isSelected = selected.has(skill.skill_code);
              return (
                <label key={skill.skill_code} className={`skill-pill ${isSelected ? "selected" : ""}`}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSkill(skill.skill_code)}
                  />
                  <span>
                    <strong>{skill.name}</strong>
                    <small>{skill.skill_code}</small>
                  </span>
                </label>
              );
            })}
            {filteredSkills.length === 0 && <p className="empty">No skills match the search.</p>}
          </div>
        </div>
        <footer className="modal-footer">
          <button type="button" className="ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="primary" onClick={() => onSave(Array.from(selected))}>
            Save changes
          </button>
        </footer>
      </div>
    </div>
  );
}
